import React, { useCallback, useMemo, useRef } from 'react';
import { client } from '../../utils/trpc-client';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  AdvancedFilterModel,
  ColGroupDef,
  ColumnRowGroupChangedEvent,
  FilterModel,
  GridApi,
  GridReadyEvent,
  SortModelItem,
  StatusPanelDef
} from '@ag-grid-community/core';
import { AgGridReact, CustomCellRendererProps, CustomTooltipProps } from '@ag-grid-community/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../shared/page-title';
import { SearchRow } from '../../components/display/search-bar';
import { showErrorToast } from '../../components/feedback/error-toast';
import {
  currencyValue,
  dateValue,
  defaultColDef,
  numberFilter,
  percentFilter,
  percentValue,
  textFilter
} from '../../components/tables/table-utils';
import { AllCampaignsIcon, PlatformBuyIcon } from '../../components/layout/nav-icons';
import { TotalStatusPanel } from '../shared/totals-status-panel';
import type { RowClickedEvent } from '@ag-grid-community/core/dist/types/src/events';
import { useCaptureListingPageView } from '../../utils/posthog/analytics';
import { zohoAccountTypeValues, zohoCampaignStatusValues } from 'shared/src/domain/zoho/zoho-types';
import {
  DoneOMeterTooltip,
  PendingPropertyIndicator
} from '../../components/tables/cell-renderers/utils';
import { parseFilterModelPerKey } from 'shared/src/utils/filter/parse-filter-model';
import {
  LineItemsWithMediaPlansIndicator,
  LineItemsWithMediaPlansTooltip,
  LineItemsWithPlatformsIndicator,
  LineItemsWithPlatformsTooltip,
  PriceAllocatedIndicator,
  PriceAllocatedTooltip
} from '../../components/tables/cell-renderers/amount-allocated';
import {
  ActiveCampaignMediaPlans,
  ActiveCampaignMediaPlansTooltip
} from '../../components/tables/cell-renderers/active-media-plans';
import { PacingIndicator } from '../../components/tables/cell-renderers/pacing-indicator';
import { PercentCompletedIndicator } from '../../components/tables/cell-renderers/percent-completed-indicator';
import {
  CampaignLineItemPlanStatusIndicator,
  CampaignMediaPlanPlanStatusIndicator
} from '../../components/tables/cell-renderers/plan-status-indicators';
import {
  CampaignLineItemsPlanStatusTooltip,
  campaignLineItemsPlanStatusTooltipValue,
  CampaignMediaPlansPlanStatusTooltip,
  campaignMediaPlanStatusTooltipValue
} from '../../components/feedback/plan-status-tooltip';
import { seniorMediaTraderRoles } from 'shared/src/utils/user/user-utils';
import { parseErrorMsg } from '../../utils/listing-utils';
import { useUrlTableState } from '../../components/tables/table-state-persistence';
import { UserFilters, useUserFilters } from '../../components/tables/use-user-filters';
import { Loading } from '../../components/feedback/loading';
import { getCurrentViewState } from '../../components/tables/saved-views-utils';
import { PercentDifferenceIndicator } from '@/components/tables/cell-renderers/percent-difference-indicator';
import {
  marginDiscountDiff,
  marginVarianceDiff
} from '@/components/tables/cell-renderers/percent-difference-utils';
import {
  CampaignFilterTypes,
  CampaignOverallResult,
  CampaignSort,
  CampaignSortColumn,
  campaignSortColumns,
  CampaignTableRow
} from 'shared/src/api/campaigns';
import { autoSizeStrategy } from '@/utils/ag-grid-utils';

type CellRendererProps<T> = CustomCellRendererProps<CampaignTableRow, T>;
export type TooltipProps<T> = CustomTooltipProps<CampaignTableRow, T>;

export function isCampaignTotalsRow(row: CampaignTableRow | undefined) {
  return row?.campaign_id === '__TOTAL_ROW__';
}

export function AllCampaigns() {
  const userFilters = useUserFilters();
  // Don't want to initialise the table until we have the user filters, otherwise we end up reinitialising it and
  // triggering multiple requests.
  return !userFilters ? <Loading /> : <AllCampaignsInner userFilters={userFilters} />;
}

function AllCampaignsInner({ userFilters }: { userFilters: UserFilters }) {
  const gridApi = useRef<GridApi<CampaignTableRow>>(undefined);
  const { initialState, storeGridState, storeSearch, getSearch } = useUrlTableState();
  const navigate = useNavigate();

  useCaptureListingPageView('campaign');
  const columnDefs = useMemo(() => createColumnDefs(userFilters), [userFilters]);
  const handleRowClick = useCallback(
    (clickEvent: RowClickedEvent<CampaignTableRow>) => {
      const { event, data } = clickEvent;
      if (!data) return;
      const url = `/campaigns/${data.campaign_id}`;
      if (isMetaKeyPressed(event)) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    },
    [navigate]
  );

  function setSearchText(search: string) {
    storeSearch(search);
    gridApi.current?.refreshServerSide({ purge: true });
  }

  const getCurrentViewStateCallback = () => {
    return getCurrentViewState(gridApi, getSearch);
  };

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApi.current = params.api;
      gridApi.current.setGridOption('serverSideDatasource', {
        getRows: async params => {
          try {
            const sort = transformSortModel(params.request.sortModel);
            const filters = transformFilterModel(params.request.filterModel);
            const page = params.request.endRow ? Math.ceil(params.request.endRow / 50) : 1;
            const requestParams = {
              page,
              sort,
              filters,
              search: getSearch(),
              rowGroupCols: params.request.rowGroupCols || [],
              groupKeys: params.request.groupKeys
            };

            const { campaigns, overall } = await client.allCampaignsV2.query(requestParams);
            const rows = getRowCount(campaigns, overall);
            params.success({
              rowData: campaigns,
              rowCount: rows
            });
            if (!params.request.groupKeys || params.request.groupKeys.length == 0) {
              params.api.setGridOption('pinnedBottomRowData', [mapTotals(overall)]);
            }
          } catch (error) {
            console.error('Failed to fetch campaigns', error);
            showErrorToast(parseErrorMsg(error));
            params.success({ rowData: [], rowCount: 0 });
            return;
          }
        }
      });
    },
    [getSearch]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(() => {
    return {
      statusPanels: [{ statusPanel: TotalStatusPanel, align: 'left' }]
    };
  }, []);

  const setPins = useCallback((gridApi: GridApi) => {
    const isGrouped = (gridApi.getRowGroupColumns()?.length ?? 0) > 0;

    if (isGrouped) {
      // Unpin everything
      const allColIds = (gridApi.getAllGridColumns() ?? []).map(c => c.getColId());
      gridApi.applyColumnState({
        state: allColIds.map(colId => ({ colId, pinned: null })),
        defaultState: { pinned: null }
      });
      // Tell grid to keep detail rows, also used as flag to remove total row count
      gridApi.updateGridOptions({ keepDetailRows: true } as any);
    } else {
      // Pin campaign_name back on the left
      gridApi.applyColumnState({
        state: [{ colId: 'campaign_name', pinned: 'left' }]
      });
      // Don't kep detail rows, display total row count
      gridApi.updateGridOptions({ keepDetailRows: false } as any);
    }
  }, []);

  const onColumnRowGroupChanged = useCallback(
    (e: ColumnRowGroupChangedEvent) => {
      setPins(e.api);
    },
    [setPins]
  );

  const autoGroupColumnDef = {
    headerName: 'Group',
    minWidth: 200,
    maxWidth: 500,
    suppressHeaderMenuButton: true,
    sortable: false,
    resizable: true,
    cellRendererParams: {
      innerRenderer: ({ value, data }: CellRendererProps<string>) => {
        if (!data?.count) return '';

        return (
          <span className="flex items-center gap-2">
            {value ? (
              <>
                <span className="font-medium">{value}</span>
                <span className="text-sm text-gray-500">({data.count.toLocaleString()})</span>
              </>
            ) : (
              <span className="italic text-gray-400">none ({data.count.toLocaleString()})</span>
            )}
          </span>
        );
      },
      suppressCount: true
    }
  };

  return (
    <div className="flex h-full w-full flex-col px-8 pb-8 pt-3">
      <PageTitle title="Bravo | Campaigns" />
      <SearchRow
        icon={<AllCampaignsIcon />}
        title="Campaigns"
        defaultQuery={getSearch()}
        placeholder="Search By Campaign"
        submitSearch={setSearchText}
        tableType="campaigns"
        getCurrentViewState={getCurrentViewStateCallback}
      />
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          rowGroupPanelShow={'onlyWhenGrouping'}
          groupDisplayType={'singleColumn'}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          defaultColDef={{
            ...defaultColDef,
            suppressHeaderMenuButton: false,
            enableRowGroup: true
          }}
          rowBuffer={0}
          rowModelType={'serverSide'}
          cacheBlockSize={50}
          cacheOverflowSize={2}
          isExternalFilterPresent={() => true}
          infiniteInitialRowCount={100}
          maxBlocksInCache={10}
          autoSizeStrategy={autoSizeStrategy}
          onGridReady={onGridReady}
          statusBar={statusBar}
          onRowClicked={handleRowClick}
          tooltipShowDelay={200}
          rowHeight={42}
          headerHeight={32}
          groupHeaderHeight={24}
          initialState={initialState}
          onStateUpdated={storeGridState}
          serverSideOnlyRefreshFilteredGroups={false}
          serverSideSortAllLevels={true}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelKey: 'columns',
                labelDefault: 'Columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressPivots: true,
                  suppressPivotMode: true,
                  suppressValues: true
                }
              },
              'filters'
            ]
          }}
        />
      </div>
    </div>
  );
}

const stageFilterParams = { values: zohoCampaignStatusValues };
const accountTypeFilterParams = { values: zohoAccountTypeValues };

function createColumnDefs(userFilters: UserFilters): ColGroupDef<CampaignTableRow>[] {
  const { mediaTraders, clientSuccessDirectors, accountManagers } = userFilters;
  return [
    {
      headerName: 'Campaign Details',
      openByDefault: false,
      children: [
        {
          field: 'campaign_name',
          headerName: 'Campaign Name',
          pinned: 'left',
          cellRenderer: (props: CustomCellRendererProps) => {
            if (props.value !== undefined) {
              return props.value;
            } else if (props.data.group === true) {
              return null;
            } else {
              return <FontAwesomeIcon icon={faSpinner} className="animate-rotate text-blue-500" />;
            }
          },
          cellStyle: { fontWeight: 'bold' },
          ...textFilter,
          maxWidth: 500
        },
        {
          field: 'campaign_number',
          headerName: 'ID',
          columnGroupShow: 'open',
          ...textFilter
        },
        {
          field: 'stage',
          filter: 'agSetColumnFilter',
          filterParams: stageFilterParams,
          columnGroupShow: 'open',
          comparator: (a, b) =>
            zohoCampaignStatusValues.indexOf(a) - zohoCampaignStatusValues.indexOf(b),
          suppressHeaderMenuButton: true
        },
        {
          field: 'account_name',
          headerName: 'Account',
          columnGroupShow: 'open',
          ...textFilter
        },
        {
          field: 'account_type',
          headerName: 'Media Campaign Type',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: accountTypeFilterParams
        },
        {
          field: 'account_industry',
          headerName: 'Industry',
          columnGroupShow: 'open',
          ...textFilter
        },
        {
          field: 'agency_name',
          headerName: 'Agency',
          columnGroupShow: 'open',
          ...textFilter
        },
        {
          field: 'price',
          headerName: 'Price',
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value, 'No Price Set'),
          suppressHeaderMenuButton: true,
          filter: 'agNumberColumnFilter',
          filterParams: {
            allowedCharPattern: '\\d\\-\\,',
            filterOptions: ['equals', 'greaterThan', 'lessThan'],
            maxNumConditions: 2
          }
        },
        {
          cellDataType: 'date',
          field: 'flight_date',
          headerName: 'Start Date',
          columnGroupShow: 'open',
          valueFormatter: params => dateValue(params.value, 'No Date Set'),
          filter: 'agDateColumnFilter',
          filterParams: {
            buttons: ['apply', 'reset'],
            filterOptions: ['lessThan', 'greaterThan', 'inRange'],
            maxNumConditions: 1
          }
        },
        {
          cellDataType: 'date',
          field: 'end_date',
          headerName: 'End Date',
          columnGroupShow: 'open',
          valueFormatter: params => dateValue(params.value, 'No Date Set'),
          filter: 'agDateColumnFilter',
          filterParams: {
            buttons: ['apply', 'reset'],
            filterOptions: ['lessThan', 'greaterThan', 'inRange'],
            maxNumConditions: 1
          }
        },
        {
          field: 'campaign_source',
          headerName: 'Marketing Source',
          columnGroupShow: 'open',
          ...textFilter
        },
        {
          field: 'region',
          headerName: 'Region',
          columnGroupShow: 'open',
          ...textFilter
        }
      ]
    },
    {
      headerName: 'Team',
      openByDefault: false,
      children: [
        {
          field: 'lead_account_owner',
          headerName: 'Lead Account Manager',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: accountManagers.map(mt => mt.name) || []
          }
        },
        {
          field: 'senior_media_trader',
          headerName: 'Sr. Media Trader',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values:
              mediaTraders
                ?.filter(mt => seniorMediaTraderRoles.includes(mt.rep_type))
                .map(mt => mt.name) || []
          }
        },
        {
          field: 'media_traders',
          headerName: 'Media Traders',
          columnGroupShow: 'open',
          valueFormatter: params => {
            return params.data?.media_traders?.map(mt => mt.name).join(',') || '';
          },
          sortable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: mediaTraders?.map(mt => mt.id) || [],
            valueFormatter: (params: { value: string }) => {
              return mediaTraders?.find(mt => mt.id === params.value)?.name ?? params.value;
            }
          },
          enableRowGroup: false
        },
        {
          field: 'client_success_director',
          headerName: 'Client Success Director',
          columnGroupShow: 'open',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: clientSuccessDirectors?.map(csd => csd.name) || []
          }
        }
      ]
    },
    {
      headerName: 'Campaign Plan',
      openByDefault: false,
      children: [
        {
          field: 'budget',
          headerName: 'Line Items',
          cellRenderer: (props: CellRendererProps<number>) => (
            <CampaignLineItemPlanStatusIndicator row={props.data} />
          ),
          tooltipComponent: CampaignLineItemsPlanStatusTooltip,
          tooltipValueGetter: campaignLineItemsPlanStatusTooltipValue,
          sortable: false,
          enableRowGroup: false
        },
        {
          field: 'line_items_with_platforms_count',
          headerName: 'Media Plans',
          cellRenderer: (props: CellRendererProps<number>) => (
            <CampaignMediaPlanPlanStatusIndicator row={props.data} />
          ),
          tooltipComponent: CampaignMediaPlansPlanStatusTooltip,
          tooltipValueGetter: campaignMediaPlanStatusTooltipValue,
          sortable: false,
          enableRowGroup: false
        },
        {
          field: 'line_item_price_allocated',
          headerName: 'Price Allocated',
          columnGroupShow: 'open',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <PriceAllocatedIndicator
              allocated={value}
              total={data?.price}
              isTotalsRow={isCampaignTotalsRow(data)}
            />
          ),
          tooltipComponent: ({ data, value }: TooltipProps<number>) => (
            <DoneOMeterTooltip value={value} isTotalsRow={isCampaignTotalsRow(data)} />
          ),
          tooltipValueGetter: ({ value, data }: TooltipProps<number>) => (
            <PriceAllocatedTooltip allocated={value} total={data?.price} />
          ),
          sortable: false,
          enableRowGroup: false
        },
        {
          field: 'line_items_with_platforms_count',
          headerName: 'Line Items w/ Platforms',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <LineItemsWithPlatformsIndicator
              allocated={value}
              total={data?.line_items_count}
              isTotalsRow={isCampaignTotalsRow(data)}
            />
          ),
          tooltipComponent: ({ data, value }: TooltipProps<number>) => (
            <DoneOMeterTooltip value={value} isTotalsRow={isCampaignTotalsRow(data)} />
          ),
          tooltipValueGetter: ({ value, data }: TooltipProps<number>) => (
            <LineItemsWithPlatformsTooltip allocated={value} total={data?.line_items_count} />
          ),
          sortable: false,
          enableRowGroup: false,
          columnGroupShow: 'open'
        },
        {
          field: 'line_items_with_media_plans_count',
          headerName: 'Line Items w/ Media Plans',
          columnGroupShow: 'open',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <LineItemsWithMediaPlansIndicator
              allocated={value}
              total={data?.line_items_count}
              missingPlatform={data?.line_items_with_platforms_count === 0}
              isTotalsRow={isCampaignTotalsRow(data)}
            />
          ),
          sortable: false,
          enableRowGroup: false,
          tooltipComponent: ({ data, value }: TooltipProps<number>) => (
            <DoneOMeterTooltip value={value} isTotalsRow={isCampaignTotalsRow(data)} />
          ),
          tooltipValueGetter: ({ value, data }: TooltipProps<number>) => (
            <LineItemsWithMediaPlansTooltip
              allocated={value}
              total={data?.line_items_count}
              missingMediaPlans={data?.total_media_plans_count === 0}
            />
          )
        }
      ]
    },
    {
      headerName: 'Media Activity',
      openByDefault: false,
      children: [
        {
          field: 'active_media_plans',
          headerName: 'Active Media Plans',
          cellRenderer: (props: CellRendererProps<number>) => (
            <ActiveCampaignMediaPlans data={props.data} />
          ),
          tooltipComponent: ({ data, value }: TooltipProps<number>) => (
            <DoneOMeterTooltip value={value} isTotalsRow={isCampaignTotalsRow(data)} />
          ),
          tooltipValueGetter: (props: TooltipProps<number>) => (
            <ActiveCampaignMediaPlansTooltip data={props.data} />
          ),
          sortable: false,
          enableRowGroup: false
        }
      ]
    },
    {
      headerName: 'Campaign Delivery',
      openByDefault: false,
      children: [
        {
          field: 'delivery_pacing',
          headerName: 'Delivery Pacing',
          enableRowGroup: false,
          cellRenderer: (props: CellRendererProps<number>) => (
            <PacingIndicator value={props.value} />
          ),
          ...percentFilter
        },
        {
          field: 'percent_revenue_delivered',
          headerName: '% Revenue Delivered',
          enableRowGroup: false,
          cellRenderer: ({ value }: CellRendererProps<number>) => (
            <PercentCompletedIndicator percentValue={value} />
          ),
          ...percentFilter
        },
        {
          field: 'billable_revenue',
          headerName: 'Eff. Billable Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'uncapped_revenue',
          headerName: 'Uncapped Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'on_pace_revenue',
          headerName: 'On-Pace Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'line_item_price_allocated',
          headerName: 'Target Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'revenue_remaining',
          headerName: 'Revenue Remaining',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'monthly_capped_actual_revenue',
          headerName: 'Monthly Capped Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'effective_monthly_capped_actual_revenue',
          headerName: 'Eff. Monthly Capped Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'lifetime_capped_actual_revenue',
          headerName: 'Lifetime Capped Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'effective_lifetime_capped_actual_revenue',
          headerName: 'Eff. Lifetime Capped Revenue',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        }
      ]
    },
    {
      headerName: 'Campaign Spend',
      openByDefault: false,
      children: [
        {
          field: 'spend_pacing',
          headerName: 'Spend Pacing',
          enableRowGroup: false,
          cellRenderer: (props: CellRendererProps<number>) => (
            <PacingIndicator value={props.value} />
          ),
          ...percentFilter
        },
        {
          field: 'percent_budget_spend',
          headerName: '% Budget Spent',
          enableRowGroup: false,
          cellRenderer: ({ value }: CellRendererProps<number>) => (
            <PercentCompletedIndicator percentValue={value} />
          ),
          ...percentFilter
        },
        {
          field: 'media_spend',
          headerName: 'Media Spend',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'on_pace_spend',
          headerName: 'On-Pace Spend',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'total_line_items_media_budget',
          headerName: 'Media Budget',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        },
        {
          field: 'budget_remaining',
          headerName: 'Budget Remaining',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter,
          resizable: false
        }
      ]
    },
    {
      headerName: 'Campaign Margin',
      openByDefault: false,
      children: [
        {
          field: 'margin_actual',
          headerName: 'Margin Actual',
          enableRowGroup: false,
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <PacingIndicator value={value} colorBase={data?.planned_margin} />
          ),
          ...percentFilter
        },
        {
          field: 'margin_variance',
          headerName: 'Margin Variance',
          enableRowGroup: false,
          cellRenderer: ({ value, data }: CellRendererProps<number>) =>
            data?.margin_actual === 0 ? (
              <PendingPropertyIndicator
                label={
                  <div className="flex items-center gap-2">
                    Pending
                    <PlatformBuyIcon />
                  </div>
                }
              />
            ) : (
              <PercentDifferenceIndicator values={marginVarianceDiff(data?.margin_actual, value)} />
            ),
          ...percentFilter
        },
        {
          field: 'target_margin_variance',
          headerName: 'Margin Discount',
          cellRenderer: ({ value, data }: CellRendererProps<number>) => (
            <PercentDifferenceIndicator values={marginDiscountDiff(data?.target_margin, value)} />
          ),
          ...percentFilter
        },
        {
          field: 'target_margin',
          headerName: 'Target Margin',
          columnGroupShow: 'open',
          valueFormatter: params => percentValue(params.value),
          ...percentFilter
        },
        {
          field: 'planned_margin',
          headerName: 'Planned Margin',
          enableRowGroup: false,
          columnGroupShow: 'open',
          valueFormatter: params => percentValue(params.value),
          ...percentFilter
        },
        {
          field: 'line_item_effective_price_allocated',
          headerName: 'Effective Price Allocated',
          enableRowGroup: false,
          columnGroupShow: 'open',
          hide: true,
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'cost_of_goods_sold',
          headerName: 'Cost of Goods Sold',
          enableRowGroup: false,
          columnGroupShow: 'open',
          hide: true,
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'total_price_margin',
          headerName: 'Total Price Margin',
          enableRowGroup: false,
          columnGroupShow: 'open',
          hide: true,
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'total_price_target_margin',
          headerName: 'Total Price Target Margin',
          enableRowGroup: false,
          columnGroupShow: 'open',
          hide: true,
          valueFormatter: params => currencyValue(params.value),
          ...numberFilter
        },
        {
          field: 'target_margin_price',
          headerName: 'Target Margin Price',
          enableRowGroup: false,
          columnGroupShow: 'open',
          hide: true,
          valueFormatter: params => percentValue(params.value),
          ...percentFilter
        }
      ]
    }
  ];
}

function transformSortModel(sortModel: SortModelItem[]): CampaignSort | undefined {
  if (sortModel.length === 0) return undefined;
  const { sort, colId } = sortModel[0];
  if (campaignSortColumns.includes(colId)) {
    // TODO[mk]: properly refine type
    return { colId: colId as CampaignSortColumn, sort };
  } else return undefined;
}

function isMetaKeyPressed(event: Event | null | undefined): boolean {
  return event instanceof PointerEvent && (event.metaKey || event.ctrlKey);
}

function transformFilterModel(filterModel: FilterModel | AdvancedFilterModel | null) {
  if (!filterModel || 'filterType' in filterModel) return {};
  return parseFilterModelPerKey(CampaignFilterTypes, filterModel);
}

function mapTotals(totals: CampaignOverallResult | null): Partial<CampaignTableRow> {
  return {
    campaign_id: '__TOTAL_ROW__',
    campaign_name: '',
    price: totals?.overall_price || 0,
    effective_price: totals?.overall_effective_price || 0,
    delivery_pacing: totals?.overall_delivery_pacing || 0,
    percent_revenue_delivered: totals?.overall_percent_revenue_delivered || 0,
    billable_revenue: totals?.overall_billable_revenue || 0,
    uncapped_revenue: totals?.overall_uncapped_revenue || 0,
    on_pace_revenue: totals?.overall_on_pace_revenue || 0,
    line_item_price_allocated: totals?.overall_line_item_price_allocated || 0,
    revenue_remaining: totals?.overall_revenue_remaining || 0,
    spend_pacing: totals?.overall_spend_pacing || 0,
    percent_budget_spend: totals?.overall_percent_budget_spend || 0,
    on_pace_spend: totals?.overall_on_pace_spend || 0,
    media_spend: totals?.overall_media_spend || 0,
    total_line_items_media_budget: totals?.overall_total_line_items_media_budget || 0,
    budget_remaining: totals?.overall_budget_remaining || 0,
    margin_actual: totals?.overall_margin_actual || 0,
    margin_variance: totals?.overall_planned_margin_variance || 0,
    planned_margin: totals?.overall_planned_margin || 0,
    target_margin: totals?.overall_target_margin || 0,
    target_margin_variance: totals?.overall_target_margin_variance || 0,
    monthly_capped_actual_revenue: totals?.overall_monthly_capped_actual_revenue || 0,
    effective_monthly_capped_actual_revenue:
      totals?.overall_effective_monthly_capped_actual_revenue || 0,
    lifetime_capped_actual_revenue: totals?.overall_lifetime_capped_actual_revenue || 0,
    effective_lifetime_capped_actual_revenue:
      totals?.overall_effective_lifetime_capped_actual_revenue || 0,
    flight_date: totals?.earliest_start_date,
    end_date: totals?.latest_end_date
  };
}

function getRowCount(campaigns: CampaignTableRow[], overall: CampaignOverallResult | null): number {
  // When grouping set the row count to -1 to remove the 'loading' icon from the grid
  return campaigns.length > 0 && campaigns[0].group ? -1 : overall?.total || 0;
}
